import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";

import TablePagination from "@mui/material/TablePagination";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import classnames from "classnames";
import tableStyles from "./table.module.css";
const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

const columnHelper = createColumnHelper();

const InvoiceListTable = ({ invoiceData }) => {
  const [data, setData] = useState(invoiceData || []);
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});

  useEffect(() => {
    setData(invoiceData || []);
  }, [invoiceData]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("num_facture", {
        header: "Numéro de facture",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("client_nom", {
        header: "Client",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("prix_total", {
        header: "Prix total Achat",
        cell: (info) => `${info.getValue()} FCFA`,
      }),
      columnHelper.accessor("on_gozem", {
        header: "Achat via Gozem",
        cell: (info) =>
          info.getValue() === "Oui" ? (
            <Chip label="Oui" color="success" size="small" variant="tonal" />
          ) : (
            <Chip label="Non" color="error" size="small" variant="tonal" />
          ),
      }),

      columnHelper.accessor("lieu_conso", {
        header: "Lieu de consommation",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("action", {
        header: "Action",
        cell: ({ row }) => (
          <Link to={`/sales/details/${row.original.id}`}>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 24 24"
              style={{ display: "block", margin: "0 auto" }}
            >
              <path
                fillRule="evenodd"
                d="M4.998 7.78C6.729 6.345 9.198 5 12 5c2.802 0 5.27 1.345 7.002 2.78a12.713 12.713 0 0 1 2.096 2.183c.253.344.465.682.618.997.14.286.284.658.284 1.04s-.145.754-.284 1.04a6.6 6.6 0 0 1-.618.997 12.712 12.712 0 0 1-2.096 2.183C17.271 17.655 14.802 19 12 19c-2.802 0-5.27-1.345-7.002-2.78a12.712 12.712 0 0 1-2.096-2.183 6.6 6.6 0 0 1-.618-.997C2.144 12.754 2 12.382 2 12s.145-.754.284-1.04c.153-.315.365-.653.618-.997A12.714 12.714 0 0 1 4.998 7.78ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                clipRule="evenodd"
              />
            </svg>{" "}
          </Link>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: { fuzzy: fuzzyFilter },
    state: { rowSelection, globalFilter },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <Card>
      <CardContent className="flex justify-between flex-col items-start md:items-center md:flex-row gap-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 is-full sm:is-auto">
          <Button
            variant="contained"
            component={Link}
            startIcon={
              <svg
                className="w-6 h-6"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 12h14m-7 7V5"
                />
              </svg>
            }
            to="/sales/add"
            style={{ backgroundColor: "orange", borderColor: "darkorange" }}
            className="p-2 px-4 rounded-md text-white hover:bg-orange-600 focus:ring-2 focus:ring-offset-2 focus:ring-orange-400 text-sm max-sm:hidden"
          >
            Créer une facture
          </Button>
        </div>
        <div className="flex max-sm:flex-col max-sm:is-full sm:items-center gap-4">
          <input
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Rechercher"
            className="max-sm:is-full sm:is-[250px] border border-gray-300 rounded-md focus:outline-none focus:ring-offset-2 focus:ring-orange-400"
          />
        </div>
      </CardContent>
      <div className="overflow-x-auto">
        <table className={tableStyles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        className={classnames({
                          "flex items-center": header.column.getIsSorted(),
                          "cursor-pointer select-none":
                            header.column.getCanSort(),
                        })}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: <i className="tabler-chevron-up text-xl" />,
                          desc: <i className="tabler-chevron-down text-xl" />,
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={classnames({ selected: row.getIsSelected() })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={table.getFilteredRowModel().rows.length}
        rowsPerPage={table.getState().pagination.pageSize}
        page={table.getState().pagination.pageIndex}
        onPageChange={(_, page) => table.setPageIndex(page)}
        onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
      />
    </Card>
  );
};

export default InvoiceListTable;
